import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getBlogPostById } from '../services/blogService';
import { BlogPost } from '../components/BlogCard';
import { ArrowLeftIcon, CalendarIcon, UserIcon } from 'lucide-react';
import { MetaTagsManager } from '../components/MetaTagsManager';
export const BlogPostPage: React.FC = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Default image if none is provided
  const defaultImage = 'https://uploadthingy.s3.us-west-1.amazonaws.com/dfdoXx5h84GymbJXQ9SBKB/air-conditioner-ventilation-installation-system-in-2025-03-27-06-10-19-utc.jpg';
  useEffect(() => {
    const fetchPost = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const data = await getBlogPostById(id);
        if (data) {
          setPost(data);
          setError(null);
        } else {
          setError('Blog post not found');
        }
      } catch (err) {
        console.error('Error fetching blog post:', err);
        setError('Failed to load blog post. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [id]);
  // Generate schema for blog post
  const generateBlogPostSchema = (post: BlogPost) => {
    return {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      image: post.imageUrl || defaultImage,
      datePublished: post.date,
      dateModified: post.date,
      author: {
        '@type': 'Person',
        name: post.author
      },
      publisher: {
        '@type': 'Organization',
        name: 'AIR-MAZING AIR, INC.',
        logo: {
          '@type': 'ImageObject',
          url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/1JGevbSoLh1AeBrRR9hz3Q/IMG_1783.jpg'
        }
      },
      description: post.excerpt,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://airmazingair.com/blog/${post.id}`
      }
    };
  };
  if (loading) {
    return <div className="flex justify-center items-center h-64 bg-gray-50 py-16" data-id="element-386">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600" data-id="element-387"></div>
      </div>;
  }
  if (error || !post) {
    return <>
        <MetaTagsManager customMetaTags={{
        title: 'Blog Post Not Found',
        description: "The blog post you're looking for could not be found.",
        canonicalUrl: '/blog',
        noIndex: true
      }} data-id="element-388" />
        <div className="bg-gray-50 py-16" data-id="element-389">
          <div className="container mx-auto px-4" data-id="element-390">
            <div className="bg-red-100 text-red-700 p-6 rounded-lg text-center max-w-lg mx-auto" data-id="element-391">
              <h1 className="text-xl font-bold mb-2" data-id="element-392">
                {error || 'Blog post not found'}
              </h1>
              <p className="mb-4" data-id="element-393">
                The blog post you're looking for could not be loaded.
              </p>
              <Link to="/blog" className="inline-flex items-center text-blue-600 hover:text-blue-800" data-id="element-394">
                <ArrowLeftIcon className="h-4 w-4 mr-2" data-id="element-395" />
                Back to Blog
              </Link>
            </div>
          </div>
        </div>
      </>;
  }
  return <>
      <MetaTagsManager customMetaTags={{
      title: post.title,
      description: post.excerpt,
      canonicalUrl: `/blog/${post.id}`,
      ogType: 'article',
      ogImage: post.imageUrl || defaultImage,
      schema: generateBlogPostSchema(post),
      keywords: post.tags?.join(', ') || ''
    }} data-id="element-396" />
      <div className="bg-gray-50 py-16" data-id="element-397">
        <div className="container mx-auto px-4" data-id="element-398">
          <div className="max-w-4xl mx-auto" data-id="element-399">
            <Link to="/blog" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-6" data-id="element-400">
              <ArrowLeftIcon className="h-4 w-4 mr-2" data-id="element-401" />
              Back to Blog
            </Link>
            <article className="bg-white rounded-xl shadow-lg overflow-hidden" data-id="element-402">
              <div className="h-96 relative" data-id="element-403">
                <img src={post.imageUrl || defaultImage} alt={post.title} className="w-full h-full object-cover" onError={e => {
                // If image fails to load, use default image
                e.currentTarget.src = defaultImage;
              }} data-id="element-404" />
                <div className="absolute top-0 right-0 bg-blue-600 text-white text-sm font-bold px-4 py-2 m-4 rounded" data-id="element-405">
                  {post.category}
                </div>
              </div>
              <div className="p-8" data-id="element-406">
                <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4" data-id="element-407">
                  {post.title}
                </h1>
                <div className="flex items-center text-gray-600 mb-8" data-id="element-408">
                  <div className="flex items-center mr-6" data-id="element-409">
                    <CalendarIcon className="h-5 w-5 mr-2 text-blue-600" data-id="element-410" />
                    <time dateTime={post.date} data-id="element-411">
                      {new Date(post.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                    </time>
                  </div>
                  <div className="flex items-center" data-id="element-412">
                    <UserIcon className="h-5 w-5 mr-2 text-blue-600" data-id="element-413" />
                    <span data-id="element-414">{post.author}</span>
                  </div>
                </div>
                <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{
                __html: post.content
              }} data-id="element-415" />
                <div className="mt-12 pt-8 border-t border-gray-200" data-id="element-416">
                  <h3 className="text-xl font-bold text-gray-800 mb-4" data-id="element-417">
                    Need help with your HVAC system?
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-418">
                    Our team of certified technicians is ready to assist with
                    all your heating and cooling needs.
                  </p>
                  <div className="flex flex-wrap gap-4" data-id="element-419">
                    <Link to="/#request-service" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md font-medium transition-colors shadow-md" data-id="element-420">
                      Request Service
                    </Link>
                    <a href="tel:2398886268" className="bg-white border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-6 py-3 rounded-md font-medium transition-colors" data-id="element-421">
                      Call: 239-888-6268
                    </a>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>
    </>;
};